import React, { useState } from 'react';
import { ShieldAlert, Mail, Phone, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button, Input, useIsMobile } from '../ui';
import api from '../../utils/api';

export default function VerificationBanner() {
  const { user, updateUser } = useAuth();
  const isMobile = useIsMobile();
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [otpStage, setOtpStage] = useState('idle'); // idle | sending | sent | verifying
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');

  if (!user || (user.is_email_verified && user.is_phone_verified)) return null;

  const resendEmail = async () => {
    setEmailSending(true);
    try {
      await api.post('/auth/resend-verification-email');
      setEmailSent(true);
    } catch { /* surfaced implicitly by button staying actionable */ }
    finally { setEmailSending(false); }
  };

  const sendOtp = async () => {
    setOtpStage('sending');
    setOtpError('');
    try {
      await api.post('/auth/send-phone-otp');
      setOtpStage('sent');
    } catch (e) {
      setOtpError(e.response?.data?.detail || 'Could not send code.');
      setOtpStage('idle');
    }
  };

  const verifyOtp = async () => {
    setOtpStage('verifying');
    setOtpError('');
    try {
      await api.post('/auth/verify-phone-otp', { code: otpCode });
      updateUser({ is_phone_verified: true });
      setOtpStage('idle');
      setOtpCode('');
    } catch (e) {
      setOtpError(e.response?.data?.detail || 'Verification failed.');
      setOtpStage('sent');
    }
  };

  return (
    <div style={{
      background: 'var(--amber-light)', borderBottom: '1px solid #FDD07A',
      padding: isMobile ? '12px 16px' : '10px 24px',
    }}>
      <div style={{
        maxWidth: '1200px', margin: '0 auto',
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '10px' : '20px', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <ShieldAlert size={16} color="var(--amber-dark)" />
          <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--amber-dark)' }}>
            Verify your account to post or respond to requests
          </span>
        </div>

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Email */}
          {user.is_email_verified ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--green-dark)', fontWeight: 600 }}>
              <CheckCircle2 size={14} /> Email verified
            </span>
          ) : emailSent ? (
            <span style={{ fontSize: '12px', color: 'var(--gray-600)' }}>
              <Mail size={12} style={{ verticalAlign: '-1px' }} /> Check your inbox for the verification link.
            </span>
          ) : (
            <Button size="sm" variant="secondary" loading={emailSending} onClick={resendEmail}>
              <Mail size={13} /> Verify email
            </Button>
          )}

          {/* Phone */}
          {user.is_phone_verified ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--green-dark)', fontWeight: 600 }}>
              <CheckCircle2 size={14} /> Phone verified
            </span>
          ) : otpStage === 'idle' ? (
            <Button size="sm" variant="secondary" onClick={sendOtp}>
              <Phone size={13} /> Verify phone
            </Button>
          ) : otpStage === 'sending' ? (
            <Button size="sm" variant="secondary" loading>Sending code…</Button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Input
                placeholder="6-digit code"
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{ width: '110px', marginBottom: 0, padding: '7px 10px', fontSize: '13px' }}
              />
              <Button size="sm" variant="primary" loading={otpStage === 'verifying'} disabled={otpCode.length !== 6} onClick={verifyOtp}>
                Confirm
              </Button>
            </div>
          )}
        </div>

        {otpError && <span style={{ fontSize: '11.5px', color: 'var(--red)', fontWeight: 500 }}>{otpError}</span>}
      </div>
    </div>
  );
}
